import { projectMemberRole } from "@/lib/projects/project-membership";
import { DEFAULT_PERMISSION_GROUPS } from "./defaults";
import type { PermissionUser, PermissionWorkspace, ProjectPermissionRole, WorkspacePermissionRole } from "./types";

export const checkProjectEntityRole = (
	entity: { readonly id: string; readonly role?: ProjectPermissionRole } | null | undefined,
	role: ProjectPermissionRole
): boolean => {
	if (!entity?.role) {
		return false;
	}

	return entity.role === role;
};

export const checkUserRole = (
	user: { readonly id: string; readonly role?: WorkspacePermissionRole } | null | undefined,
	role: WorkspacePermissionRole
): boolean => {
	if (!user?.role) {
		return false;
	}

	return user.role === role;
};

export const isProjectManager = (
	uid: string,
	workspace: PermissionWorkspace | null | undefined,
	projectId?: string
): boolean =>
	!!workspace?.projectPermissions?.find((permission) => {
		if (projectId && permission.projectId !== projectId) {
			return false;
		}
		const userGroupIds = (workspace.groups ?? [])
			.filter((group) => group.users?.some((user) => user.id === uid))
			.map((group) => group.id);
		return projectMemberRole(uid, permission.users ?? [], permission.groups ?? [], userGroupIds) === "manager";
	});

export const hasPermission = (
	permissionId: string,
	userSettings: PermissionUser,
	project?: { readonly id: string }
): boolean => {
	if (DEFAULT_PERMISSION_GROUPS.Everyone === permissionId) {
		return true;
	}

	const workspace = userSettings.workspace;
	if (!workspace) {
		return false;
	}

	const isWorkspaceAdmin = !!workspace.users?.find((user) => user.admin && user.id === userSettings.id);
	if (isWorkspaceAdmin) {
		return true;
	}

	const isManager = isProjectManager(userSettings.id, workspace);
	if (DEFAULT_PERMISSION_GROUPS.ProjectManager === permissionId && isManager) {
		return true;
	}

	const groupMember = workspace.groups
		?.find((group) => group.id === permissionId)
		?.users?.find((user) => userSettings.id === user.id);
	if (groupMember) {
		return true;
	}

	if (!project) {
		return false;
	}

	const projectPerm = workspace.projectPermissions?.find((permission) => permission.projectId === project.id);
	if (!projectPerm) {
		return false;
	}

	return (
		!projectPerm.private ||
		!!projectPerm.users?.find((user) => user && user.id === userSettings.id && !user.removed) ||
		!!projectPerm.groups?.find(
			(group) =>
				group &&
				!group.removed &&
				workspace.groups?.find(
					(workspaceGroup) =>
						workspaceGroup.id === group.id &&
						workspaceGroup.users?.find((groupUser) => groupUser.id === userSettings.id)
				)
		)
	);
};
